import { parseDefaultInstruction } from './defaultAnalyzer.js';
import { classifySstLine } from './lineClassifier.js';
import { scanSstContent } from './specialCharScanner.js';

const KEYWORDS = [
  'DEFINE',
  'WINDOW',
  'ENDWINDOW',
  'MENU',
  'ENDMENU',
  'IF',
  'THEN',
  'ELSE',
  'ENDIF',
  'CALL',
  'GOTO',
  'WRITE',
  'READ',
  'COMPUTE',
  'REQUIRE',
  'DEFAULT',
  'EXIT',
];

function createBaseDataJson(title) {
  return {
    title,
    forms: [],
    logic: [],
    remarks: [],
    calls: [],
    menus: [],
  };
}

function normalizeLine(rawLine) {
  return rawLine.trim().replace(/\s+/g, ' ');
}

function getIndent(rawLine = '') {
  const match = String(rawLine || '').match(/^\s*/);
  return match ? match[0].length : 0;
}

function enrichLineClassification(lineClassification, scannedLine, normalizedLine) {
  return {
    ...lineClassification,
    raw: scannedLine.raw,
    normalized: normalizedLine,
    normalizedRaw: normalizedLine,
    indent: getIndent(scannedLine.raw),
    hasSpecialChars: scannedLine.hasSpecialChars,
    specialChars: scannedLine.specialChars,
    hasSpecialSequences: scannedLine.hasSpecialSequences || false,
    specialSequences: scannedLine.specialSequences || [],
    diagnostics: lineClassification.diagnostics || [],
    scannerDiagnostics: scannedLine.diagnostics || [],
  };
}

function detectKeyword(line) {
  const normalized = normalizeLine(line);
  return KEYWORDS.find((keyword) => {
    return normalized === keyword || normalized.startsWith(`${keyword} `) || normalized.startsWith(`${keyword}[`);
  });
}

function parseDefine(line, lineNumber, lineClassification = {}) {
  if (lineClassification.parseStatus === 'parsed') {
    return {
      type: 'variable',
      name: lineClassification.target,
      sourceVariable: lineClassification.sourceVariable,
      suffix: lineClassification.suffix,
      datatype: lineClassification.datatype,
      length: lineClassification.length,
      required: Boolean(lineClassification.required),
      label: lineClassification.label,
      line: lineNumber,
      raw: line,
    };
  }

  const match = line.match(/^DEFINE\s*\[([A-Z0-9_]+)=([A-Z0-9_]+)(?::(\d+))?/i);

  if (!match) {
    return {
      type: 'define',
      line: lineNumber,
      raw: line,
    };
  }

  return {
    type: 'variable',
    name: match[1],
    datatype: match[2],
    length: match[3] ? Number(match[3]) : null,
    required: /(?::|^)MAND(?::|\])/i.test(line),
    line: lineNumber,
    raw: line,
  };
}

function parseLabel(line, lineNumber, lineClassification) {
  return {
    type: 'label',
    name: lineClassification.name,
    line: lineNumber,
    raw: line,
  };
}

function parseButtonDefinition(line, lineNumber, lineClassification) {
  return {
    type: 'button_definition',
    button: lineClassification.button,
    label: lineClassification.label,
    line: lineNumber,
    raw: line,
  };
}

function parseWindow(line, lineNumber, index) {
  const explicitId = line.match(/^WINDOW(?:\s+([A-Z0-9_-]+))?/i);
  const title = line.match(/"([^"]+)"/);

  return {
    type: 'window',
    id: explicitId && explicitId[1] ? explicitId[1] : `form${index}`,
    title: title ? title[1] : null,
    fields: [],
    line: lineNumber,
    raw: line,
  };
}

function parseMenu(line, lineNumber, index) {
  const explicitId = line.match(/^MENU\s+([A-Z0-9_-]+)\b(?!\s*=)/i);
  const explicitIdValue = explicitId && !['R', 'C', 'H'].includes(explicitId[1].toUpperCase()) ? explicitId[1] : null;
  const title = line.match(/\bH\s*=\s*"([^"]*)"/i) || line.match(/"([^"]+)"/);
  const row = line.match(/\bR\s*=\s*(\d+)/i);
  const column = line.match(/\bC\s*=\s*(\d+)/i);

  return {
    type: 'menu',
    id: explicitIdValue || `MENU_${index}`,
    title: title ? title[1] : 'MENU',
    row: row ? Number(row[1]) : null,
    column: column ? Number(column[1]) : null,
    items: [],
    line: lineNumber,
    raw: line,
  };
}

function parseCommand(keyword, line, lineNumber) {
  return {
    type: keyword.toLowerCase(),
    expression: line.slice(keyword.length).trim(),
    line: lineNumber,
    raw: line,
  };
}

function parseRead(line, lineNumber, lineClassification) {
  return {
    ...parseCommand('READ', line, lineNumber),
    source: lineClassification.source,
    row: lineClassification.row,
    column: lineClassification.column,
    search: lineClassification.search,
    target: lineClassification.target,
    targets: lineClassification.targets || [],
    variables: lineClassification.variables || [],
    literals: lineClassification.literals || [],
    until: lineClassification.until,
    parseStatus: lineClassification.parseStatus,
    diagnostic: lineClassification.diagnostic,
    diagnostics: lineClassification.diagnostics || [],
  };
}

function hasLineContinuation(line) {
  return /\+\s*$/.test(line);
}

function removeLineContinuation(line) {
  return line.replace(/\+\s*$/, '').trim();
}

function parseDefault(line, lineNumber, endLine, rawLines) {
  const node = parseCommand('DEFAULT', line, lineNumber);
  const parsedDefault = parseDefaultInstruction(line);

  return {
    ...node,
    ...parsedDefault,
    endLine,
    lines: rawLines,
  };
}

function parseRemark(line, lineNumber) {
  return {
    type: 'remark',
    text: line.replace(/^(REM|REMARK)\b\s*/i, ''),
    line: lineNumber,
    raw: line,
  };
}

function parseClassifiedRemark(line, lineNumber, lineClassification) {
  return {
    type: 'remark',
    remarkType: lineClassification.remarkType || 'Historical',
    template: lineClassification.template,
    skip: lineClassification.skip !== false,
    text: lineClassification.template,
    line: lineNumber,
    raw: line,
  };
}

function parseFormat(line, lineNumber) {
  return {
    type: 'format',
    line: lineNumber,
    raw: line,
  };
}

function parseSabreSpecialSequence(line, lineNumber, lineClassification) {
  return {
    type: 'sabre_special_sequence',
    sequence: lineClassification.sequence,
    sequences: lineClassification.sequences || [],
    visible: false,
    parseStatus: lineClassification.parseStatus || 'parsed',
    line: lineNumber,
    raw: line,
  };
}

function parseSabreCommand(line, lineNumber) {
  return {
    type: 'command',
    expression: line,
    line: lineNumber,
    raw: line,
  };
}

function parseSsr(line, lineNumber, lineClassification) {
  return {
    type: 'ssr',
    expression: line,
    line: lineNumber,
    raw: line,
    lineClassification,
  };
}

function parseSubroutine(line, lineNumber, lineClassification) {
  return {
    type: 'subroutine',
    name: lineClassification.name,
    children: [],
    line: lineNumber,
    raw: line,
    lineClassification,
  };
}

function withLineClassification(node, lineClassification) {
  return {
    ...node,
    raw: lineClassification.raw ?? node.raw,
    normalizedRaw: lineClassification.normalizedRaw || lineClassification.normalized || node.raw,
    indent: lineClassification.indent ?? getIndent(lineClassification.raw ?? node.raw),
    hasSpecialChars: lineClassification.hasSpecialChars || false,
    specialChars: lineClassification.specialChars || [],
    hasSpecialSequences: lineClassification.hasSpecialSequences || false,
    specialSequences: lineClassification.specialSequences || [],
    visible: lineClassification.visible,
    lineClassification,
  };
}

export function parseSst(content, title = 'sin-nombre.sst') {
  const scannedContent = scanSstContent(content);
  const ast = {
    type: 'program',
    nodes: [],
    specialCharReport: scannedContent.report,
    specialCharDiagnostics: scannedContent.diagnostics,
    specialCharLines: scannedContent.lines
      .filter((line) => line.hasSpecialChars)
      .map(({ diagnostics, ...line }) => line),
  };
  const dataJson = createBaseDataJson(title);
  const lines = scannedContent.lines;
  let activeWindow = null;
  let windowCount = 0;
  let menuCount = 0;
  let menuDepth = 0;

  for (let index = 0; index < lines.length; index += 1) {
    const lineNumber = index + 1;
    let scannedLine = lines[index];
    let line = normalizeLine(scannedLine.normalized);

    if (!line) {
      if (scannedLine.hasSpecialChars) {
        ast.lineClassifications = ast.lineClassifications || [];
        ast.lineClassifications.push({
          line: lineNumber,
          type: 'blank',
          visible: false,
          raw: scannedLine.raw,
          normalized: line,
          normalizedRaw: line,
          hasSpecialChars: scannedLine.hasSpecialChars,
          specialChars: scannedLine.specialChars,
          hasSpecialSequences: scannedLine.hasSpecialSequences || false,
          specialSequences: scannedLine.specialSequences || [],
          scannerDiagnostics: scannedLine.diagnostics || [],
        });
      }

      continue;
    }

    let lineClassification = enrichLineClassification(
      classifySstLine(line, {
        inWindow: Boolean(activeWindow),
        inMenu: menuDepth > 0,
      }),
      scannedLine,
      line
    );

    ast.lineClassifications = ast.lineClassifications || [];
    ast.lineClassifications.push({
      line: lineNumber,
      ...lineClassification,
    });

    if (lineClassification.type === 'sabre_special_sequence') {
      const specialSequenceNode = withLineClassification(parseSabreSpecialSequence(line, lineNumber, lineClassification), lineClassification);
      ast.nodes.push(specialSequenceNode);
      dataJson.logic.push(specialSequenceNode);
      continue;
    }

    if (lineClassification.type === 'format') {
      ast.nodes.push(withLineClassification(parseFormat(line, lineNumber), lineClassification));
      continue;
    }

    if (lineClassification.type === 'remark') {
      const remarkNode = withLineClassification(parseClassifiedRemark(line, lineNumber, lineClassification), lineClassification);
      ast.nodes.push(remarkNode);
      dataJson.remarks.push(remarkNode);
      continue;
    }

    if (lineClassification.type === 'command') {
      const commandNode = withLineClassification(parseSabreCommand(line, lineNumber), lineClassification);
      ast.nodes.push(commandNode);
      dataJson.logic.push(commandNode);
      continue;
    }

    if (lineClassification.type === 'ssr') {
      const ssrNode = withLineClassification(parseSsr(line, lineNumber, lineClassification), lineClassification);
      ast.nodes.push(ssrNode);
      dataJson.logic.push(ssrNode);
      continue;
    }

    if (lineClassification.type === 'subroutine') {
      const subroutineNode = withLineClassification(parseSubroutine(line, lineNumber, lineClassification), lineClassification);
      ast.nodes.push(subroutineNode);
      dataJson.logic.push(subroutineNode);
      continue;
    }

    if (lineClassification.type === 'label') {
      const labelNode = withLineClassification(parseLabel(line, lineNumber, lineClassification), lineClassification);
      ast.nodes.push(labelNode);
      dataJson.logic.push(labelNode);
      continue;
    }

    if (lineClassification.type === 'button_definition') {
      const buttonDefinitionNode = withLineClassification(parseButtonDefinition(line, lineNumber, lineClassification), lineClassification);
      ast.nodes.push(buttonDefinitionNode);
      dataJson.logic.push(buttonDefinitionNode);
      continue;
    }

    if (/^(REM|REMARK)\b/i.test(line)) {
      const remark = withLineClassification(parseRemark(line, lineNumber), lineClassification);
      ast.nodes.push(remark);
      dataJson.remarks.push(remark);
      continue;
    }

    const keyword = detectKeyword(line);

    if (!keyword) {
      ast.nodes.push(withLineClassification({
        type: 'unknown',
        line: lineNumber,
        raw: line,
      }, lineClassification));
      continue;
    }

    if (keyword === 'DEFINE') {
      const variable = withLineClassification(parseDefine(line, lineNumber, lineClassification), lineClassification);
      ast.nodes.push(variable);

      if (activeWindow) {
        activeWindow.fields.push(variable);
      }

      continue;
    }

    if (keyword === 'WINDOW') {
      windowCount += 1;
      const windowNode = withLineClassification(parseWindow(line, lineNumber, windowCount), lineClassification);
      activeWindow = {
        id: windowNode.id,
        fields: [],
      };
      windowNode.fields = activeWindow.fields;
      ast.nodes.push(windowNode);
      dataJson.forms.push(activeWindow);
      continue;
    }

    if (keyword === 'MENU') {
      menuCount += 1;
      const menuNode = withLineClassification(parseMenu(line, lineNumber, menuCount), lineClassification);
      activeWindow = null;
      ast.nodes.push(menuNode);
      menuDepth += 1;
      dataJson.menus.push({
        id: menuNode.id,
        title: menuNode.title,
        items: [],
      });
      continue;
    }

    if (keyword === 'ENDWINDOW') {
      const node = withLineClassification(parseCommand(keyword, line, lineNumber), lineClassification);
      ast.nodes.push(node);
      activeWindow = null;
      continue;
    }

    if (keyword === 'ENDMENU') {
      const node = withLineClassification(parseCommand(keyword, line, lineNumber), lineClassification);
      ast.nodes.push(node);
      menuDepth = Math.max(0, menuDepth - 1);
      continue;
    }

    if (keyword === 'DEFAULT') {
      const rawLines = [scannedLine.raw];
      const normalizedLines = [line];

      while (hasLineContinuation(line) && index + 1 < lines.length) {
        index += 1;
        scannedLine = lines[index];
        line = normalizeLine(scannedLine.normalized);
        rawLines.push(scannedLine.raw);
        normalizedLines.push(line);
      }

      const concatenatedLine = normalizedLines.map(removeLineContinuation).join('');
      const node = withLineClassification(parseDefault(concatenatedLine, lineNumber, index + 1, rawLines), lineClassification);
      node.normalizedLines = normalizedLines;
      ast.nodes.push(node);
      dataJson.logic.push(node);
      continue;
    }

    if (keyword === 'READ') {
      const node = withLineClassification(parseRead(line, lineNumber, lineClassification), lineClassification);
      ast.nodes.push(node);
      dataJson.logic.push(node);
      continue;
    }

    const node = withLineClassification(parseCommand(keyword, line, lineNumber), lineClassification);
    ast.nodes.push(node);

    if (keyword === 'CALL') {
      dataJson.calls.push(node);
      continue;
    }

    dataJson.logic.push(node);
  }

  return {
    ast,
    dataJson,
  };
}
